/**
 * The two ways a car name is compared — never mix them.
 *
 * - `catalogKey` — identity of a name in the catalogue: uniqueness
 *   (`name_norm` on the server), search, and resolving what a user typed
 *   («Mercedes Benz» ≡ «Mercedes-Benz» ≡ «MERCEDESBENZ», «Citroën» ≡
 *   «CITROEN»). Separators and case do not matter; letters do.
 * - `ocrFold` — scanner only. Folds the Cyrillic capitals a recogniser uses
 *   for Latin ones (a Russian-locale Vision reads the «К» of «SKODA» as
 *   Cyrillic) so a mixed-script token still meets its key. It would merge
 *   real Cyrillic words with Latin ones, so it is never used for identity.
 *
 * Both strip combining marks, so «Й» ≡ «И» and «Ў» ≡ «У»: acceptable for a
 * key (no two makes differ only by a breve) and inherited by the server's
 * `name_norm` uniqueness. `ё` is mapped to `е` explicitly, before `normalize`,
 * so the result does not depend on the runtime's Unicode tables.
 */
const COMBINING_MARKS = /[̀-ͯ]/g;
const KEY_SEPARATORS = /[\s\-.'’`ʼ_/]+/g;
/**
 * `String.prototype.normalize` is platform-backed on Hermes (ICU on Android,
 * CFString on iOS). A build without it returns the input unchanged; the
 * explicit `ё` mapping above keeps the common case correct even then.
 */
const stripMarks = (text) => {
    const decomposed = typeof text.normalize === 'function' ? text.normalize('NFKD') : text;
    return decomposed.replace(COMBINING_MARKS, '');
};
const yoToYe = (text) => text.replace(/ё/g, 'е').replace(/Ё/g, 'Е');
/** Identity key of a make / model / alias name. */
export const catalogKey = (name) => stripMarks(yoToYe(name)).toLowerCase().replace(KEY_SEPARATORS, '');
/** Cyrillic capitals drawn identically to a Latin one. */
const CYRILLIC_TO_LATIN = {
    А: 'A',
    В: 'B',
    Е: 'E',
    К: 'K',
    М: 'M',
    Н: 'H',
    О: 'O',
    Р: 'P',
    С: 'C',
    Т: 'T',
    У: 'Y',
    Х: 'X',
};
const CYRILLIC_LOOKALIKES = /[АВЕКМНОРСТУХ]/g;
/**
 * One OCR token, folded for matching: capitals, marks stripped, Cyrillic
 * look-alikes → Latin. Apply it to BOTH the row token and the key it is
 * compared with, or a key holding «Й» / a Cyrillic letter can never match.
 */
export const ocrFold = (token) => stripMarks(yoToYe(token).toUpperCase()).replace(CYRILLIC_LOOKALIKES, (ch) => CYRILLIC_TO_LATIN[ch] ?? ch);
//# sourceMappingURL=normalize.js.map